import { dehydrate, hydrate, type DehydratedState, type QueryClient, type QueryKey } from "@tanstack/react-query";

/**
 * The dashboard's saved view: the last answers to the account's own dashboard
 * reads, kept in this browser so a return visit paints at once and refreshes
 * quietly (`AD-DSH02`).
 *
 * Measured 2026-09-24: every visit to a dashboard page waited on its reads
 * before showing anything, although the answers were almost always what the
 * last visit had already seen. Now a page opens on those answers and the reads
 * run behind them; each one keeps its own `staleTime`, so what was already
 * stale when saved is fetched again the moment the page mounts.
 *
 * The rules that make it safe to keep an account's data in the browser:
 *
 * - it is restored only after the API has confirmed who is signed in, and only
 *   for that same account id: never before the session check, never for
 *   somebody else;
 * - signing out, a refused session and an account switch throw it away, and an
 *   impersonation or a contest temporary account neither writes nor reads it;
 * - only the reads named in `SAVED_READS` are kept, all of them the account's
 *   own lists, never a token or a credential;
 * - a new deploy starts from nothing (`NEXT_PUBLIC_BUILD_ID`), so a component
 *   never meets data shaped for the build before it;
 * - an answer older than a week is dropped, and the whole view is capped.
 *
 * Storage may be missing or throw (private windows, blocked site data, a full
 * quota); every reader and writer treats that as "nothing saved".
 */

export const SAVED_VIEW_KEY = "progsity:saved-view";

const FORMAT = 1;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Characters of JSON, roughly bytes: a fifth of a typical 5 MB origin quota. */
const MAX_CHARS = 1_000_000;
const SAVE_DELAY_MS = 2_000;

const BUILD = process.env["NEXT_PUBLIC_BUILD_ID"] || "dev";

/** Key prefixes of the reads a return visit opens on. */
const SAVED_READS: ReadonlyArray<readonly string[]> = [
  ["me", "stats"],
  ["me", "library"],
  ["me", "orders"],
  ["me", "live-classes"],
  ["me", "notifications"],
  ["me", "announcements"],
  ["me", "skill-profile"],
  ["dashboard", "overview"],
  ["gamification", "profile"],
  ["gamification", "myBadges"],
  ["gamification", "badgeCatalog"],
  ["gamification", "leaderboard"],
  ["gamification", "activityPages"],
  ["certificates", "mine"],
  ["quiz", "myHistory"],
  ["quiz", "creatorList"],
  ["quiz", "mySubscription"],
  ["assignments", "mine"],
  ["assignments", "creatorList"],
  ["judge", "my-submissions"],
  ["studio", "summary"],
  ["contest-platform", "authoring"],
];

export function isSavedRead(queryKey: QueryKey): boolean {
  return SAVED_READS.some((prefix) => prefix.every((part, index) => queryKey[index] === part));
}

type SavedQuery = DehydratedState["queries"][number];
type SavedView = { format: number; build: string; userId: string; queries: SavedQuery[] };
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function read(storage: StorageLike): SavedView | null {
  try {
    const raw = storage.getItem(SAVED_VIEW_KEY);
    if (!raw) return null;
    const view = JSON.parse(raw) as SavedView;
    if (!view || typeof view.userId !== "string" || !Array.isArray(view.queries)) return null;
    return view;
  } catch {
    return null;
  }
}

function current(view: SavedView | null, userId: string): view is SavedView {
  return !!view && view.format === FORMAT && view.build === BUILD && view.userId === userId;
}

// Bumped by `forgetSavedView`, so a save already scheduled for the session
// that just ended can never write after it.
let generation = 0;

export function forgetSavedView(storage: StorageLike | null = defaultStorage()): void {
  generation += 1;
  try {
    storage?.removeItem(SAVED_VIEW_KEY);
  } catch {
    // Nothing to do: an unremovable view is still refused for any other account.
  }
}

/**
 * Puts the account's saved answers into the cache. Call it only once the API
 * has confirmed `userId`. Returns how many answers it restored.
 */
export function restoreSavedView(
  client: QueryClient,
  userId: string,
  now = Date.now(),
  storage: StorageLike | null = defaultStorage(),
): number {
  if (!storage) return 0;
  const view = read(storage);
  if (!view) return 0;
  if (!current(view, userId)) {
    forgetSavedView(storage);
    return 0;
  }
  const queries = view.queries.filter(
    (query) =>
      query?.state?.status === "success" &&
      now - query.state.dataUpdatedAt < MAX_AGE_MS &&
      Array.isArray(query.queryKey) &&
      isSavedRead(query.queryKey),
  );
  hydrate(client, { mutations: [], queries });
  return queries.length;
}

/**
 * Keeps the saved view in step with the cache for this account: shortly after
 * any kept read answers, and when the page is hidden. Answers the cache has
 * since let go of stay saved until they age out. Returns the unsubscribe.
 */
export function startSavingView(
  client: QueryClient,
  userId: string,
  storage: StorageLike | null = defaultStorage(),
): () => void {
  if (!storage) return () => {};
  const ownGeneration = generation;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const write = () => {
    timer = null;
    if (generation !== ownGeneration) return;
    const now = Date.now();
    const previous = read(storage);
    const kept = current(previous, userId) ? previous.queries : [];
    const live = dehydrate(client, {
      shouldDehydrateQuery: (query) => query.state.status === "success" && isSavedRead(query.queryKey),
    }).queries;
    const byHash = new Map(kept.map((query) => [query.queryHash, query]));
    for (const query of live) byHash.set(query.queryHash, query);
    let queries = [...byHash.values()]
      .filter((query) => now - query.state.dataUpdatedAt < MAX_AGE_MS)
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
    let text = JSON.stringify({ format: FORMAT, build: BUILD, userId, queries } satisfies SavedView);
    while (text.length > MAX_CHARS && queries.length > 0) {
      queries = queries.slice(0, -1);
      text = JSON.stringify({ format: FORMAT, build: BUILD, userId, queries } satisfies SavedView);
    }
    try {
      storage.setItem(SAVED_VIEW_KEY, text);
    } catch {
      // A full quota just means the next visit waits for its reads.
    }
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") return;
    if (!isSavedRead(event.query.queryKey)) return;
    if (!timer) timer = setTimeout(write, SAVE_DELAY_MS);
  });
  const onHidden = () => {
    if (document.visibilityState !== "hidden" || !timer) return;
    clearTimeout(timer);
    write();
  };
  document.addEventListener("visibilitychange", onHidden);

  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", onHidden);
    if (timer) clearTimeout(timer);
    timer = null;
  };
}
